import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Loader2, ShieldAlert, Radar, MailCheck } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/nexus";
import brandIcon from "@/assets/tentacles-icon.png";

export default function Login() {
  const navigate = useNavigate();
  const { login, register } = useAuth();
  const [mode, setMode] = useState("login"); // login | register
  const [form, setForm] = useState({ email: "", password: "", name: "" });
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setNotice("");
    setBusy(true);
    try {
      if (mode === "login") {
        await login(form.email, form.password);
        navigate("/", { replace: true });
      } else {
        await register(form.email, form.password, form.name);
        setNotice("Account created. An administrator must approve your access before you can sign in.");
        setMode("login");
        setForm({ email: form.email, password: "", name: "" });
      }
    } catch (err) {
      setError(formatApiError(err, "Authentication failed. Check your credentials."));
    } finally {
      setBusy(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <div className="min-h-screen bg-nexus-bg text-nexus-text flex">
      {/* Left brand panel */}
      <div className="hidden lg:flex flex-col justify-between w-[46%] border-r border-nexus-border bg-gradient-to-br from-octopi-indigo/70 via-nexus-panel to-octopi-purple/30 p-12 relative overflow-hidden">
        <div className="absolute inset-0 opacity-[0.05]" style={{ backgroundImage: "radial-gradient(circle at 1px 1px, #7E498B 1px, transparent 0)", backgroundSize: "22px 22px" }} />
        <div className="flex items-center gap-3 relative">
          <img src={brandIcon} alt="Tentacles" className="w-10 h-10 rounded-[9px] shadow-[0_0_18px_rgba(126,73,139,0.6)]" />
          <div>
            <div className="font-rajdhani font-bold text-xl tracking-[0.3em] uppercase leading-none">
              Tentacles
            </div>
            <div className="text-[10px] font-mono text-nexus-text-3 tracking-widest mt-1">AN OCTOPI AI PRODUCT</div>
          </div>
        </div>
        <div className="relative">
          <div className="panel-title mb-3"><span className="dot" /> Agentic Commerce Intelligence</div>
          <h1 className="font-rajdhani font-bold text-4xl leading-tight tracking-tight">
            The intelligence layer for<br /><span className="text-octopi-purple">agentic payments</span>.
          </h1>
          <p className="text-[13px] text-nexus-text-2 mt-4 max-w-md leading-relaxed">
            Inventory agent wallets, map on-chain A2A commerce, correlate against declared intent, and
            drill from a global map to street-level infrastructure — powered by live Etherscan &amp; Blockscout feeds.
          </p>
          <div className="flex gap-2 mt-6 text-[10px] font-mono uppercase tracking-widest text-nexus-text-3">
            <span className="border border-nexus-border px-2 py-1">Force Graphs</span>
            <span className="border border-nexus-border px-2 py-1">NLP Query</span>
            <span className="border border-nexus-border px-2 py-1">On-Chain Intel</span>
          </div>
        </div>
        <div className="text-[10px] font-mono text-nexus-text-3 relative">OCTOPI.AI // SECURED ACCESS</div>
      </div>

      {/* Right auth form */}
      <div className="flex-1 flex items-center justify-center p-6">
        <div className="w-full max-w-sm panel brackets p-7" data-testid="login-panel">
          <span className="b1" /><span className="b2" />
          <div className="flex items-center gap-2 mb-1">
            <Radar size={14} className="text-nexus-cyan" />
            <div className="panel-title">{mode === "login" ? "Operator Sign-In" : "Request Access"}</div>
          </div>
          <h2 className="font-rajdhani font-bold text-2xl tracking-tight mb-5">
            {mode === "login" ? "Access the command center" : "Request an operator account"}
          </h2>

          <button
            data-testid="google-login-button"
            onClick={googleLogin}
            className="w-full flex items-center justify-center gap-2 border border-nexus-border hover:border-nexus-cyan/60 hover:bg-nexus-cyan/5 py-2.5 text-[12px] font-mono uppercase tracking-widest transition-colors"
          >
            <svg width="15" height="15" viewBox="0 0 24 24"><path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1Z"/><path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.65l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84A11 11 0 0 0 12 23Z"/><path fill="#FBBC05" d="M5.84 14.11a6.6 6.6 0 0 1 0-4.22V7.05H2.18a11 11 0 0 0 0 9.9l3.66-2.84Z"/><path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1A11 11 0 0 0 2.18 7.05l3.66 2.84C6.71 7.3 9.14 5.38 12 5.38Z"/></svg>
            Continue with Google
          </button>

          <div className="flex items-center gap-3 my-5">
            <div className="flex-1 h-px bg-nexus-border" />
            <span className="text-[10px] font-mono uppercase tracking-widest text-nexus-text-3">or email</span>
            <div className="flex-1 h-px bg-nexus-border" />
          </div>

          <form onSubmit={submit} className="space-y-3">
            {mode === "register" && (
              <label className="block">
                <span className="panel-title">Full name</span>
                <input data-testid="login-name-input" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="mt-1 w-full bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono focus:border-nexus-cyan/60 outline-none" />
              </label>
            )}
            <label className="block">
              <span className="panel-title">Email</span>
              <input data-testid="login-email-input" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                className="mt-1 w-full bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono focus:border-nexus-cyan/60 outline-none" />
            </label>
            <label className="block">
              <span className="panel-title">Password</span>
              <input data-testid="login-password-input" type="password" required minLength={mode === "register" ? 8 : undefined} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })}
                className="mt-1 w-full bg-nexus-bg border border-nexus-border px-3 py-2 text-[13px] font-mono focus:border-nexus-cyan/60 outline-none" />
              {mode === "register" && <span className="text-[10px] font-mono text-nexus-text-3">min 8 characters</span>}
            </label>

            {notice && (
              <div data-testid="login-notice" className="flex items-start gap-1.5 text-[11px] font-mono text-nexus-cyan border border-nexus-cyan/40 bg-nexus-cyan/5 px-2 py-1.5">
                <MailCheck size={12} className="mt-0.5 shrink-0" /> {notice}
              </div>
            )}
            {error && (
              <div data-testid="login-error" className="flex items-start gap-1.5 text-[11px] font-mono text-nexus-rose border border-nexus-rose/40 bg-nexus-rose/5 px-2 py-1.5">
                <ShieldAlert size={12} className="mt-0.5 shrink-0" /> {error}
              </div>
            )}

            <button data-testid="login-submit-button" type="submit" disabled={busy}
              className="w-full flex items-center justify-center gap-2 bg-nexus-cyan/10 border border-nexus-cyan/60 text-nexus-cyan hover:bg-nexus-cyan/20 py-2.5 text-[12px] font-mono uppercase tracking-widest transition-colors disabled:opacity-50">
              {busy && <Loader2 size={13} className="animate-spin" />}
              {mode === "login" ? "Sign In" : "Create Account"}
            </button>
          </form>

          <button
            data-testid="toggle-auth-mode"
            onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
            className="w-full text-center text-[11px] font-mono text-nexus-text-2 hover:text-nexus-cyan mt-4"
          >
            {mode === "login" ? "No account? Request access →" : "Already registered? Sign in →"}
          </button>
        </div>
      </div>
    </div>
  );
}
